// Phase F — "My Cash" view.
//
// Lists every booking whose collector_user_id matches the current user.
// Shows a running subtotal of cash held but not yet reconciled (i.e. paid
// bookings where the cash is still with the collector). Empty state nudges
// the manager to link a Paid Method when no jobs are routed yet.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Wallet, AlertCircle } from "lucide-react";

function currency(n, cur = "EUR") {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-IE", { style: "currency", currency: cur }).format(v);
  } catch {
    return `${cur} ${v.toFixed(2)}`;
  }
}

function formatStart(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IE", { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function MyCash() {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: meData } = await api.get("/auth/me");
        setMe(meData);
        const uid = meData?.id || meData?.manager_id || meData?.user_id;
        if (!uid) {
          setRows([]);
          setLoading(false);
          return;
        }
        const { data } = await api.get("/bookings", {
          params: { collector_user_id: uid, page_size: 500 },
        });
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        setErrMsg("Couldn't load your cash jobs. Check your connection and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    // Cash being held = paid bookings whose collector still owes the
    // business a reconciliation (UI heuristic — proxy via payment_status).
    let holding = 0;
    let holdingCount = 0;
    for (const b of rows) {
      if (b.payment_status === "paid") {
        const amt = Number(b.subtotal ?? b.total ?? 0);
        if (amt > 0) {
          holding += amt;
          holdingCount += 1;
        }
      }
    }
    return { holding, holdingCount };
  }, [rows]);

  if (loading) {
    return (
      <div className="container max-w-4xl px-4 py-8" data-testid="my-cash-loading">
        <div className="text-sm text-muted-foreground">Loading your cash jobs…</div>
      </div>
    );
  }

  if (errMsg) {
    return (
      <div className="container max-w-4xl px-4 py-8">
        <div className="bg-rose-50 border border-rose-300 text-rose-900 rounded-md p-3 flex items-center gap-2">
          <AlertCircle size={16} /> {errMsg}
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl px-4 py-6 space-y-6" data-testid="my-cash-tab">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <Wallet size={22} className="text-accent" /> My Cash
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bookings collected through Paid Methods linked to {me?.name || me?.email || "you"}.
          </p>
        </div>
      </header>

      {/* Running total banner — only shown when the collector is actually
          holding cash for the business. Empty case shows nothing here. */}
      {totals.holdingCount > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 text-amber-900 rounded-[8px] p-4 flex items-center justify-between gap-4"
          data-testid="my-cash-holding-banner"
        >
          <div>
            <div className="text-[11px] mono uppercase tracking-widest">You are holding</div>
            <div className="text-2xl font-extrabold tabular-nums mono">
              {currency(totals.holding)}
            </div>
            <div className="text-xs">
              across {totals.holdingCount} job{totals.holdingCount === 1 ? "" : "s"} awaiting reconciliation
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div
          className="bg-card border border-border rounded-[8px] p-6 text-sm text-muted-foreground"
          data-testid="my-cash-empty-state"
        >
          No cash jobs assigned to you yet. Ask your manager to link a Paid Method
          to your account in <strong>Settings → Paid Methods</strong>.
        </div>
      ) : (
        <div
          className="bg-card border border-border rounded-[8px] divide-y divide-border"
          data-testid="my-cash-list"
        >
          {rows.map((b) => (
            <Link
              key={b.id}
              to={`/bookings/${b.id}`}
              className="block px-4 py-3 hover:bg-secondary/40 transition-colors"
              data-testid={`my-cash-row-${b.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
                      {b.booking_code}
                    </span>
                    <span className="font-semibold truncate">{b.customer_name || "—"}</span>
                    {b.payment_status === "paid" ? (
                      <span className="mono text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">
                        paid
                      </span>
                    ) : (
                      <span className="mono text-[10px] uppercase tracking-widest bg-muted px-1.5 py-0.5 rounded">
                        {(b.payment_status || "").replace(/_/g, " ") || "—"}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5 truncate">
                    {formatStart(b.scheduled_start)}
                    {b.location?.postcode ? ` · ${b.location.postcode}` : ""}
                    {b.paid_method_name ? ` · via ${b.paid_method_name}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-extrabold tabular-nums mono text-sm">
                    {currency(b.subtotal ?? b.total ?? 0)}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
                    {b.payment_type || ""}
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground shrink-0" aria-hidden="true" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
